// Filter for inbound Slack message events before they reach an agent's
// session (ENG-4484). Slack's `message` event type fires for many sub-event
// classes (edits, deletes, joins, pins, ephemeral bot posts, etc.), most of
// which arrive with no text and no resolvable user. Forwarding them caused
// agents to reply "Got an empty message from you — what did you need?"
// because the pipeline pushed a blank `<channel>` notification into the
// session.
//
// The filter only runs for `type === 'message'` events. `app_mention` always
// passes upstream — explicit @mention is the strongest possible signal the
// user wants a response.

import { hasForwardedContent } from './slack-forwarded.js';

export interface SlackMessageEvent {
  type: string;
  subtype?: string;
  user?: string;
  /**
   * ENG-8669 follow-up: apps that post via a webhook carry `bot_id` and NO
   * `user`. Previously read only via inline casts; declared here because the
   * third-party grant below has to match on it.
   */
  bot_id?: string;
  text?: string;
  files?: unknown[];
  blocks?: unknown[];
  /** ENG-6949: forwarded/shared message content lives here, not in `text`. */
  attachments?: unknown[];
  ts?: string;
  /**
   * ENG-5843: sender's workspace id, populated by Slack on every event.
   * For Slack Connect channels (shared between workspaces), this is the
   * workspace the SENDER is in, not the host workspace of the channel.
   * The internal_only gate compares this against SLACK_HOME_TEAM_ID to
   * drop messages from external Connect workspaces.
   */
  team?: string;
  /** Slack message metadata — present when posted with the metadata API. */
  metadata?: {
    event_type?: string;
    event_payload?: Record<string, unknown>;
  };
  /**
   * ENG-9416: the edited message, present only on `subtype: 'message_changed'`.
   *
   * Slack nests the real content here and leaves the OUTER event with no `user`
   * and no `text` — so a `message_changed` that got past the subtype gate would
   * die on `no_user` immediately afterwards. `evt.ts` is the EDIT's timestamp;
   * `message.ts` is the ORIGINAL message's, and that distinction is what lets a
   * stateful caller ask "has this one already been answered".
   */
  message?: {
    type?: string;
    subtype?: string;
    user?: string;
    bot_id?: string;
    text?: string;
    files?: unknown[];
    blocks?: unknown[];
    attachments?: unknown[];
    ts?: string;
    thread_ts?: string;
    /**
     * Set when a PERSON (or an app acting as one) changed the text. Slack's own
     * rewrites — a link unfurl being the common one — arrive as
     * `message_changed` with no `edited` block, and that is the discriminator
     * this module uses to keep dropping them.
     */
    edited?: { user?: string; ts?: string };
  };
}

export type FilterDecision =
  | { forward: true }
  | { forward: false; reason: 'subtype' | 'no_user' | 'empty' | 'sender_policy' };

// Plain user messages arrive with no `subtype`. `file_share` is a user
// attaching a file. `thread_broadcast` is a thread reply mirrored to the
// channel. Everything else (message_changed, message_deleted, channel_join,
// channel_leave, bot_message, pin_added, etc.) is dropped.
const ALLOWED_MESSAGE_SUBTYPES = new Set<string | undefined>([
  undefined,
  'file_share',
  'thread_broadcast',
]);

/**
 * ENG-8669 follow-up: the minimum an allowlist entry has to expose for this
 * filter to recognise it. Structural on purpose — `SlackThirdPartyBotEntry`
 * satisfies it without this module importing the peer classifier, which would
 * be a cycle.
 */
export interface ThirdPartyBotGrant {
  bot_user_id: string;
  bot_id?: string;
}

/**
 * ENG-8669 shipped a third-party bot allowlist at governance (layer 3) that
 * production traffic never reached, because THIS filter is layer 1 and
 * `inbound-access` returns on the first drop. A Linear/PagerDuty/CloudWatch
 * post arrives as `subtype: 'bot_message'` with a `bot_id` and no `user`, so it
 * died here on `subtype` (and would have died again on `no_user`) long before
 * the allowlist was consulted. The feature was inert for exactly the bots it
 * named.
 *
 * The two gates are relaxed ONLY for a bot that is already on the allowlist, so
 * the exception is exactly as wide as the grant and not one message wider:
 *
 *   - no `thirdPartyBots` argument, or no matching entry -> behaviour is
 *     byte-identical to before this change. Every existing caller that does not
 *     opt in keeps today's semantics.
 *   - `subtype` is relaxed for `'bot_message'` ONLY. A `message_deleted` or
 *     `message_changed` from an allowlisted bot still drops, because the grant
 *     is "this bot may wake us", not "this bot may bypass the noise filter".
 *   - the `empty` check is never relaxed. An allowlisted bot posting nothing is
 *     still nothing.
 *
 * Matching accepts either id. Slack calls the `B…` value on the event `bot_id`
 * while an app's bot user is a `U…`; operators reliably have the former and not
 * always the latter, and silently ignoring the id they can actually find is how
 * this defect stayed invisible.
 */
function matchesThirdPartyGrant(
  evt: SlackMessageEvent,
  thirdPartyBots: ReadonlyArray<ThirdPartyBotGrant> | undefined,
): boolean {
  const botId = evt.bot_id;
  if (!botId || !thirdPartyBots?.length) return false;
  return thirdPartyBots.some((b) => b.bot_id === botId || b.bot_user_id === botId);
}

/**
 * ENG-9416 — the edited message a `message_changed` event is really about, or
 * `null` when this is not a human edit.
 *
 * `message_changed` sat in the dropped-subtype set, indiscriminately. The guard
 * is right for three of the four things that arrive under that subtype and
 * wrong for the fourth, which is also the most common of the four: a person
 * fixes a typo, or adds the @mention they forgot, on a message the agent has
 * not answered. Measured in cody's stderr log — ~25 drops in 40 minutes on
 * 21 Aug, eight more on 23 Aug. Steady state, not an incident.
 *
 * What makes it worse than its siblings is WHERE it happens. A
 * `reason=content:subtype` drop is before a pending marker exists, so none of
 * the recovery machinery downstream can help and none of the pending-inbound
 * alarms can fire. It is the earliest and quietest of the silent-loss routes,
 * which is why an agent could be dropping messages while carrying zero open
 * alerts.
 *
 * THE DISCRIMINATOR IS `edited`, NOT THE TEXT. Slack's own rewrites arrive with
 * no `edited` block, so they stay dropped. A human edit carries
 * `edited: { user, ts }`.
 *
 * And the edit is judged on what the message NOW SAYS, not on what changed:
 * the case that makes this ticket's framing right is a user adding the
 * @mention they forgot. The original is legitimately not-for-the-agent and is
 * dropped by sender policy; the edit that makes it for-the-agent must be seen.
 * A diff-based test ("did the text change") would miss exactly that one.
 *
 * DELIBERATELY NOT DECIDED HERE: whether the agent already answered the
 * original. That is state, this function is pure, and it has a second caller —
 * `slack-sender-gate.ts`, whose whole purpose is to mirror the live handler
 * faithfully, and whose own comment says a silent divergence between the two
 * "is the same class of defect as the one being fixed". Unwrapping here keeps
 * them in step; the answered/unanswered split belongs to the channel, which
 * holds the markers.
 *
 * Generic over the input so the wider event types used by callers (with
 * `channel`, `thread_ts`, …) keep their extra fields: the envelope's routing
 * is spread through, and the inner message's own fields win.
 */
export function humanEditedMessage<T extends SlackMessageEvent>(evt: T): T | null {
  if (evt.type !== 'message' || evt.subtype !== 'message_changed') return null;
  const inner = evt.message;
  if (!inner?.edited) return null;
  const merged = { ...evt } as Record<string, unknown>;
  delete merged['message'];
  delete merged['subtype'];
  for (const [k, v] of Object.entries(inner)) {
    if (k === 'edited' || v === undefined) continue;
    merged[k] = v;
  }
  merged['type'] = 'message';
  // THE EDIT'S OWN IDENTITY, kept alongside the original's `ts`.
  //
  // The two are needed for different things and conflating them breaks one of
  // them. `ts` stays the ORIGINAL's, because that is the message a reply must
  // thread against and the key any "have I answered this" lookup uses. But an
  // edit is a DIFFERENT EVENT from the original, and `slackInboundDedupId`
  // namespaces by event type for exactly that reason (ENG-8387: the
  // `app_mention` and its `message` echo share a `ts`, and keying on the shared
  // value let one claim the other's slot).
  //
  // Without this the synthetic would carry `type: 'message'` and the original's
  // `ts`, so its dedup id would be the original's - and an edit of a message
  // that was already delivered would be dropped as a duplicate. That is the
  // defect this change exists to fix, reintroduced one layer down.
  if (evt.ts !== undefined) merged['edit_ts'] = evt.ts;
  return merged as T;
}

export function decideSlackMessageForward(
  evt: SlackMessageEvent,
  thirdPartyBots?: ReadonlyArray<ThirdPartyBotGrant>,
): FilterDecision {
  if (evt.type !== 'message') {
    // Non-message events (app_mention etc.) don't go through this filter.
    return { forward: true };
  }
  // ENG-9416: a human edit is judged by the SAME rules as any other message,
  // on what it now says. An unfurl or other Slack-side rewrite carries no
  // `edited` block, returns null here, and falls through to the subtype drop
  // below exactly as before.
  const edited = humanEditedMessage(evt);
  if (edited) return decideSlackMessageForward(edited, thirdPartyBots);
  const granted = matchesThirdPartyGrant(evt, thirdPartyBots);
  if (!ALLOWED_MESSAGE_SUBTYPES.has(evt.subtype)) {
    // ENG-8669 follow-up: `bot_message` only, and only for a granted bot.
    if (!granted || evt.subtype !== 'bot_message') {
      return { forward: false, reason: 'subtype' };
    }
  }
  if (!evt.user && !granted) {
    return { forward: false, reason: 'no_user' };
  }
  const hasText = typeof evt.text === 'string' && evt.text.trim().length > 0;
  const hasFiles = Array.isArray(evt.files) && evt.files.length > 0;
  const hasBlocks = Array.isArray(evt.blocks) && evt.blocks.length > 0;
  // ENG-6949: a bare forward (user shares a message with no note of their own)
  // arrives with empty text/files/blocks but real content in `attachments`.
  const hasAttachments = hasForwardedContent(evt.attachments);
  if (!hasText && !hasFiles && !hasBlocks && !hasAttachments) {
    return { forward: false, reason: 'empty' };
  }
  return { forward: true };
}

/**
 * ENG-6378 — is this `message` event the redundant ECHO of an `app_mention`?
 *
 * When a user @mentions the bot in a channel, Slack delivers TWO events for the
 * SAME underlying message, sharing one `ts`: an `app_mention` (the actionable
 * event the agent should answer) and a `message` whose text carries the
 * `<@BOTID>` literal. The `app_mention` is the canonical delivery; the `message`
 * twin carries nothing new. Slack fires `app_mention` on EXACTLY that literal,
 * so whenever this returns `true` an `app_mention` is GUARANTEED to exist —
 * dropping the echo can never lose content.
 *
 * Why drop it (rather than rely on the channel:ts dedup): the dedup keys on
 * `channel:ts` only, and the two events share `ts`. On a Slack reconnect the
 * echo can arrive BEFORE the app_mention, mark the shared `ts`, then get dropped
 * downstream by the `mention_only` response-mode filter — and the actionable
 * `app_mention` is then suppressed as a "duplicate", so the agent never sees the
 * mention (the ENG-6378 silent-loss bug, confirmed on agt-aws-1). Dropping the
 * echo here — at ingress, before the dedup and before command dispatch — is
 * order-independent: the app_mention can never be the one that loses the race.
 *
 * It also subsumes two ENG-6270 cases: the thread/auto-follow double of an
 * @mention (the auto_followed `message` mentions the bot → dropped, app_mention
 * delivers) and the doubled command-exec (the echo can't reach `handle*Command`).
 * A pure auto-follow thread message that does NOT mention the bot is untouched
 * (no `<@BOTID>` → returns false → delivered as before).
 *
 * Excludes DMs: a DM arrives as a `message` event with NO `app_mention` twin, so
 * a bot-mentioning DM must NEVER be dropped here. `botUserId` null (pre-connect)
 * → `false` (fail open to existing behaviour).
 */
export function isAppMentionEcho(
  evt: { type?: string; text?: string; channel?: string },
  botUserId: string | null | undefined,
): boolean {
  if (evt.type !== 'message') return false; // app_mention is never its own echo
  if (!botUserId) return false; // pre-connect / unknown → don't drop (fail open)
  if (evt.channel?.startsWith('D')) return false; // DM: no app_mention twin exists
  // `mentionsUser`, not a bare `includes('<@id>')`. THIRD site of the same
  // asymmetry CodeRabbit caught at the other two (#5695), and this one bites in
  // the direction ENG-10079 is about: an echo the dedup fails to RECOGNISE is
  // not dropped, so a mention written `<@BOT|display>` delivers the message
  // twice — once as `app_mention`, once as its `message` twin. A ticket whose
  // subject is agents answering the same message more than once should not
  // leave a duplicate-delivery path open on the piped form.
  return typeof evt.text === 'string' && mentionsUser(evt.text, botUserId);
}

/**
 * ENG-8387 — the fresh-ingress dedup id for one Slack event, namespaced by
 * EVENT TYPE as well as channel + ts.
 *
 * The id used to be `channel:ts`, which is NOT unique per event: an `app_mention`
 * and its `message` echo are two distinct events sharing one `ts`. Collapsing
 * them in the dedup is what made ENG-6378's silent loss possible — whichever
 * arrived first claimed the shared id, and if that was the echo (which is then
 * dropped downstream by the `mention_only` gate) the actionable `app_mention`
 * was suppressed as a "duplicate" and the mention was never delivered.
 *
 * `isAppMentionEcho` above fixes that by dropping the echo at ingress by
 * IDENTITY, which is order-independent — but ONLY while `botUserId` is resolved.
 * It fails open on an unresolved id (`!botUserId → false`), which is exactly the
 * post-restart pre-connect window, and on a host with a high restart rate that
 * window opens constantly. Fail-open is the right posture for *delivering* a
 * message; it is the wrong posture when the thing failing open is the
 * suppression of a duplicate whose survival causes a silent drop. Namespacing
 * the id removes the shared-key collision underneath, so the echo can no longer
 * claim the `app_mention`'s slot no matter what `botUserId` is doing.
 *
 * What this deliberately does NOT do: it does not collapse the mention/echo
 * pair. That job belongs to `isAppMentionEcho` (identity, correct) and,
 * durably, to ENG-6380 (mark the dedup at CONFIRMED PUSH rather than at raw
 * ingress, so an event that never reached the agent can never claim an id at
 * all). In the fail-open window BOTH events now survive the dedup, so a
 * configuration that delivers non-mention channel messages (`response_mode`
 * other than the default `mention_only`, or notify-dispatch
 * `membership`/`filter`) can see the pair delivered twice. That is the
 * deliberate trade and it runs in the same direction as the dedup module's own
 * doctrine: a redundant delivery is recoverable, a swallowed one is not.
 *
 * Same-type re-arrival — a Socket Mode reconnect redelivering the identical
 * `app_mention`, the case the guard actually exists for — still dedups exactly
 * as before, because the type component is identical on both copies.
 *
 * Returns `null` when there is nothing stable to dedup on (missing channel or
 * ts); the caller then delivers, per the guard's fail-open contract.
 */
export function slackInboundDedupId(evt: {
  type?: string;
  channel?: string;
  ts?: string;
  /** ENG-9416: set by `humanEditedMessage` — the EDIT event's own timestamp. */
  edit_ts?: string;
}): string | null {
  if (!evt.channel || !evt.ts) return null;
  // ENG-9416: an EDIT is its own event. `ts` here is the original message's, so
  // without this an edit would claim the original's slot and be dropped as a
  // duplicate - the very silent loss this change removes, one layer down. The
  // edit's own ts makes each edit a distinct id, so a redelivery of the SAME
  // edit still collapses and a second, later edit still gets through.
  if (evt.edit_ts) return `${evt.channel}:${evt.ts}:message_edit:${evt.edit_ts}`;
  // `type` is present on every events_api event; `unknown` keeps the id stable
  // (and still same-type-collapsing) rather than silently widening the key.
  return `${evt.channel}:${evt.ts}:${evt.type ?? 'unknown'}`;
}

// ENG-4595: should this agent arm the response-timeout for an inbound
// message? Returns false only for the "discretionary auto-follow" case —
// a thread message that this agent is auto-following but is not
// explicitly addressed in. Arming a timer in that case is the bug behind
// the multi-agent cascade where one agent's "fell on the floor" timeout
// message lands as a thread message and other auto-following agents
// process it as user input. Pulled out as a pure function so the policy
// is testable independently of the live channel.
//
// ENG-4789: this only governs the response-timer arm; the 👀 ack
// reaction is decoupled (see decideSlackAckReaction) so auto-followed
// thread replies still get the "I saw it" feedback even when the agent
// will silently skip the reply.
export interface EngagementInputs {
  type: string;
  text: string;
  isAutoFollowed: boolean;
  // ENG-5592: nullable — the bot user_id now resolves asynchronously, so it
  // may be null for the brief window before resolution completes. A null id
  // simply can't match an explicit `<@id>` mention (the `app_mention` type
  // still does), so engagement falls back to the auto-followed heuristic.
  botUserId: string | null;
}

export function decideSlackEngagement(input: EngagementInputs): boolean {
  // ENG-10079 (review round 2, CodeRabbit + Brad Bond): `mentionsUser`, not a
  // bare `includes('<@id>')`. The piped `<@id|display>` form is the same
  // mention and Slack picks which one it sends, so the bare check made an
  // EXPLICIT self-mention in an auto-followed thread read as not-addressed -
  // `shouldEngage` false, and with no prior participation in the thread
  // `decideMarkerArm` then returns false too. That is a delivery failure for a
  // message that names this agent by id, and it predates ENG-10079: this gate
  // has always been the one that decides whether an auto-followed thread is
  // ours to answer.
  const isExplicitMention =
    input.type === 'app_mention' ||
    (!!input.botUserId && mentionsUser(input.text, input.botUserId));
  return !(input.isAutoFollowed && !isExplicitMention);
}

// ENG-6319: should this inbound arm a pending-inbound marker? Markers are
// the trigger for the ENTIRE delivery-protection stack (ghost-reply Stop
// hook, ENG-6289 park + orient surfacing, ⏳ ack-age, watchdog give-up,
// sweeps) — a delivered-but-unmarked message is invisible to all of it.
// Historically arming was gated on `decideSlackEngagement` alone, which
// left auto-followed un-mentioned thread replies unprotected: an operator
// conversing with the agent inside a thread (e.g. an alert-channel alarm
// thread) gets ZERO ghost-reply recovery (observed live: sherlock,
// 2026-06-11, composed reply never sent, silent loss).
//
// The widened rule: also arm when the bot is a conversational PARTICIPANT
// in the thread — it has posted there (sticky `bot_posted` on the persisted
// ThreadEntry, set on every successful slack.reply) or started it. True
// side-conversations (bot auto-following but never spoke) still arm
// nothing, preserving the ENG-4595 posture. Note: markers are timer-less
// since ENG-5490 — arming cannot post anything by itself; participant
// markers are additionally written `discretionary: true` so the noisy
// consumers (⏳ ack-age, give-up notices, wedge live-scan) can exclude
// them while recovery/park keep working.
export interface MarkerArmInputs {
  shouldEngage: boolean;
  isAutoFollowed: boolean;
  /** Bot has posted in (or started) this thread — from the tracked-thread store. */
  botPostedInThread: boolean;
  /**
   * ENG-10079: the message @-mentions a DIFFERENT registered agent and not this
   * one. Optional so every existing caller is unchanged; absent reads as false.
   */
  addressesAnotherAgent?: boolean;
}

/**
 * ENG-10079: a message that names another agent must not arm a marker for THIS
 * one, however chatty this agent has been in the thread.
 *
 * WHAT WENT WRONG. Samuel @-mentioned one agent; four answered inside ninety
 * seconds, and the CEO noticed. The participation clause above is why: once an
 * agent has posted in a thread, `botPostedInThread` is sticky, so EVERY later
 * message in that thread armed it a marker — including one addressed by name to
 * somebody else. Nothing downstream re-reads the addressee.
 *
 * WHY THE MARKER IS THE RIGHT PLACE TO FIX IT, rather than the reply. A marker
 * is the trigger for the whole delivery-protection stack, and two of those
 * consumers are what actually produced the incident:
 *
 *   - the ghost-reply Stop hook rescues an agent's end-of-turn narration into
 *     the thread of the last pending marker. That is how internal commentary —
 *     including, in one case, commentary about an entirely different thread —
 *     gets posted at a user (CS-1444 is the same shape).
 *   - a pending marker is surfaced to the agent as an inbound it still owes a
 *     reply to, which is a standing nudge to answer a question meant for a
 *     colleague.
 *
 * Remove the marker and you remove this message's contribution to both. You
 * cannot stop a model calling `slack.reply` from out here without taking the
 * tool away; you can stop manufacturing the pressure and the rescue target.
 * That is the honest scope of this fix and it is deliberately not claimed as a
 * guarantee of silence.
 *
 * AND THE SCOPE IS NARROWER STILL — "no marker, no rescue" IS NOT TRUE
 * (review, Brad Bond). The Stop hook does not look up "the marker for this
 * inbound". It reads channel + thread_ts off the LAST `<channel>` tag in the
 * transcript, then globs `<channel>__<thread_ts>__*.json` and takes the first
 * match: the OLDEST pending marker in that thread. `message_ts` is not in the
 * prefix, so nothing ever asks whether the tag it took its coordinates from
 * had a marker of its own.
 *
 * Two things follow, and both are pinned by tests in
 * `packages/core/src/__tests__/stop-hook-script.test.ts` rather than left as
 * prose:
 *
 *   - suppression here is ARM-ONLY. It never revokes an existing marker, so if
 *     the thread already owed a reply, the rescue target is still there and
 *     end-of-turn text can still be posted into that thread. Suppressing this
 *     message's marker does not disable recovery.
 *   - the reach stops at the thread. The prefix carries `thread_ts`, and a
 *     top-level message is its own thread, so a marker elsewhere in the same
 *     CHANNEL is not a target.
 *
 * So what this actually buys is the case where the suppressed message is the
 * ONLY thing that would have made the thread a rescue target at all — which is
 * the incident, and is worth having, but is not the same claim.
 *
 * THE CASE THIS MUST NOT BREAK, and it is why the guard is written against
 * `shouldEngage` rather than as a bare early return: "@don @maven can you both
 * take a look". That names another agent AND this one, so `shouldEngage` is
 * true, and the marker must arm exactly as before. Suppression applies only
 * where this agent was not addressed at all.
 */
export function decideMarkerArm(input: MarkerArmInputs): boolean {
  // Addressed to someone else and not to us: arm nothing. Ordered before the
  // participation clause deliberately — participation is precisely the state
  // that used to override it.
  if (input.addressesAnotherAgent && !input.shouldEngage) return false;
  return input.shouldEngage || (input.isAutoFollowed && input.botPostedInThread);
}

/**
 * ENG-10079 follow-up (review, Brad Bond) — which mentions in `text` are
 * ADDRESSEES, as opposed to people merely referred to?
 *
 * The first cut of this treated any `<@Uxxx>` anywhere in the message as an
 * addressee, and that is wrong in a way that costs a real message. In an
 * ongoing conversation with this agent:
 *
 *     "Can you review <@U0ANSA3HHPZ>'s changes before we merge?"
 *
 * names Don but is asking US. Under the old predicate that suppressed our own
 * marker. Reproduced true-before / false-after in this suite.
 *
 * THE SIGNAL. A Slack address is positional: it opens the message. "@don can
 * you look" addresses Don; "ask @don about it" refers to him. So an addressee
 * is a mention in the LEADING RUN of the first line that is not quoted
 * material — optionally after one short greeting token, because "hey @don can
 * you look" is plainly an address and dropping that case would gut the fix.
 * Everything else is a reference.
 *
 * FOUR THINGS THIS DELIBERATELY REFUSES TO CALL AN ADDRESS:
 *
 *   - a possessive: "<@don>'s changes" opens the message but is talking about
 *     him, not to him. The run stops on the apostrophe.
 *   - anything mid-sentence: "so I think <@don> should take this". A false
 *     negative here costs a marker that should not have armed, which is
 *     exactly today's behaviour; a false positive costs a message the agent
 *     genuinely owed. Not symmetric, so ties go to delivering.
 *   - anything inside quoted material: a `>` blockquote or a fenced code
 *     block is somebody ELSE'S message being pasted in. Whoever it addressed,
 *     it is not addressing anyone now.
 *   - a bare id with no mention syntax. `U0ANSA3HHPZ` in prose is a string.
 *
 * The pipe form Slack sometimes renders (`<@U123|display>`) is matched too.
 * The rest of this module compares with `includes('<@' + id + '>')`, which
 * silently misses it; this parser does not, and where the two disagree this
 * one is the conservative direction (it sees an addressee the other missed,
 * and seeing THIS bot as an addressee is what suppresses suppression).
 */
const MENTION_AT_START = /^<@([A-Z0-9]+)(?:\|[^>]*)?>/;

/**
 * Does `text` mention `userId` ANYWHERE, in either form Slack sends?
 *
 * ENG-10079 (review, Brad Bond). `<@U123>` and `<@U123|display>` are the same
 * mention; Slack picks the form. The first cut taught `leadingAddresseeIds` the
 * piped form and then left the "are we mentioned" guard on a bare
 * `includes('<@' + id + '>')` in the very same function - so:
 *
 *     <@U0DON> investigate; <@U0MARLOW|marlow> review the fix
 *
 * failed to see Marlow, found Don in the leading run, and suppressed the marker
 * of an agent that had been addressed by name. Asymmetry in the wrong
 * direction: the strict half was the half protecting delivery.
 *
 * Anchored on the id so `<@U0MARLOW>` can never be matched by a prefix of a
 * longer id, and `[|>]` closes the alternation so `<@U0MARLOWX>` cannot satisfy
 * a search for `U0MARLOW`.
 */
export function mentionsUser(text: string, userId: string): boolean {
  if (!userId) return false;
  const escaped = userId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`<@${escaped}[|>]`).test(text);
}

/**
 * Enumerated on purpose rather than generalised into "skip any short word".
 * A vocative opener is a closed set in practice; a heuristic that skips
 * arbitrary leading words would turn "Can you review @don's PR" back into an
 * address, which is the exact regression this exists to prevent.
 */
const GREETING_PREFIX = /^(?:hey|hi|hello|yo|ok|okay|cc|thanks|thx|morning|afternoon)\b[\s,:!.-]*/i;

/** Separators that can sit BETWEEN addressees in one leading run. */
const ADDRESSEE_SEPARATOR = /^(?:[\s,:;]+|and\b|&|\+)+/i;

/**
 * The region of `text` an address could live in: the first line that is not a
 * blockquote and is not inside a fenced code block. Returns '' when there is
 * no such line (a message that is nothing but quoted material addresses
 * nobody).
 *
 * `&gt;` IS THE FORM SLACK ACTUALLY SENDS. Slack reserves `&`, `<` and `>` and
 * escapes them in event text, which is why a mention survives as the literal
 * control sequence `<@U…>` while a user-typed blockquote arrives as `&gt; ...`.
 * (`slack-channel.ts` says the same from the outbound side: its entity decoder
 * "deliberately refuses `&amp;`/`&lt;`/`&gt;` - Slack reserves those".)
 *
 * Being precise about what matching it buys, because the obvious claim is
 * wrong: a bare-`>` check is NOT dead code that lets quoted mentions through.
 * An unrecognised `&gt;` prefix pushes the mention out of position 0, so
 * `leadingAddresseeIds` returns nothing and a single-line quote is already
 * treated as addressing nobody - by accident, but correctly. The case that
 * genuinely needs this is a quote FOLLOWED BY a real address:
 *
 *     &gt; <@don> are you online?
 *     <@don> over to you
 *
 * Without the escaped form, `addressRegion` stops on the quoted line, finds no
 * addressee, and the second line - an actual address - is never read. That is
 * a false negative in the suppression, and it is the case the test asserts,
 * because it is the only one a mutation can distinguish.
 *
 * Backticks are NOT reserved, so a fence arrives as literal ``` and needs no
 * equivalent.
 */
const BLOCKQUOTE_PREFIX = /^(?:&gt;|>)/;

function addressRegion(text: string): string {
  // The fence is tracked by LENGTH, not as a boolean (ENG-10079 review round 4).
  // `slackMessageText` grows a code block's fence past the longest backtick run
  // inside it, so a pasted sample containing ``` is wrapped in ````. Toggling on
  // any run of three would then let the block's own inner fence close the
  // wrapper early and leak its tail back into prose - which is the bug the
  // fence exists to prevent, reintroduced for the likeliest input.
  let fenceLen = 0;
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    const run = /^`{3,}/.exec(line)?.[0].length ?? 0;
    if (run > 0) {
      if (fenceLen === 0) fenceLen = run;
      else if (run >= fenceLen) fenceLen = 0; // only an equal-or-longer run closes
      continue;
    }
    if (fenceLen > 0) continue;
    if (BLOCKQUOTE_PREFIX.test(line)) continue; // Slack blockquote, escaped or not
    if (line.length === 0) continue;
    return line;
  }
  return '';
}

/**
 * The Slack user ids this message is ADDRESSED to, in order. Exported so the
 * addressing rule can be tested directly rather than only through its effect
 * on a marker.
 */
export function leadingAddresseeIds(text: string): string[] {
  let rest = addressRegion(text);
  const greeting = GREETING_PREFIX.exec(rest);
  if (greeting) rest = rest.slice(greeting[0].length);

  const ids: string[] = [];
  for (;;) {
    const m = MENTION_AT_START.exec(rest);
    const id = m?.[1];
    if (!m || !id) break;
    const after = rest.slice(m[0].length);

    // A possessive turns the mention into a reference: "<@don>'s changes".
    //
    // A bare word character running straight on from the mention does NOT, and
    // an earlier cut of this got that wrong (ENG-10079 review round 2). Slack
    // never renders `<@U…>` glued to a following word - but the Block Kit
    // flattener does: `richTextToString` joins an inline run with '' and trims
    // each child, so a `rich_text_section` of [mention, " over to you"] arrives
    // as `<@U…>over to you`. Treating that as a reference made the addressing
    // rule silently weaker on messages typed in Slack's OWN composer, which is
    // the common path, not an edge case.
    if (/^['’]/.test(after)) break;

    ids.push(id);

    if (after.length === 0) break; // bare "@don"
    const sep = ADDRESSEE_SEPARATOR.exec(after);
    if (!sep) break; // punctuation that is not a separator ends the run
    rest = after.slice(sep[0].length);
  }
  return ids;
}

/**
 * ENG-10079: is this message ADDRESSED to a registered agent other than this
 * one, without also being addressed to this one?
 *
 * `registeredAgentIds` is the same registry the sender policy already builds
 * (the union of curated `SLACK_PEERS` and the same-team roster). It was only
 * ever used to bind the *sender* label on bot-authored posts; this reads it as
 * the set of ADDRESSEES, which is the question nothing was asking.
 *
 * Deliberately conservative — four ways it returns false rather than guess:
 *   - the registry is empty or unknown (a solo agent, or a manager that has not
 *     wired the roster yet): nothing to compare against, so behaviour is
 *     exactly what it is today.
 *   - the text mentions THIS bot ANYWHERE, addressed or merely referenced, in
 *     EITHER `<@id>` or `<@id|display>` form. A mention of us is enough to stop
 *     suppressing, whatever its grammar; this check stays deliberately wider
 *     than the addressing rule below it, and it must never be the STRICTER of
 *     the two - see `mentionsUser`.
 *   - this bot's own id is unresolved (`null`): we cannot prove we were not
 *     addressed, so we must not act as though we were not.
 *   - the other agent is only REFERRED to rather than addressed (the review
 *     finding above).
 *
 * A false negative here costs a marker that should not have armed — today's
 * behaviour. A false positive costs a message the agent genuinely owed, which
 * is the failure mode ENG-9069 exists for. They are not symmetric, so the ties
 * go to delivering.
 */
export function addressesAnotherRegisteredAgent(
  text: string,
  botUserId: string | null,
  registeredAgentIds: ReadonlySet<string> | undefined,
): boolean {
  if (!botUserId) return false;
  if (!registeredAgentIds || registeredAgentIds.size === 0) return false;
  // BOTH mention forms, and anywhere in the message. See `mentionsUser`.
  if (mentionsUser(text, botUserId)) return false;

  const addressees = leadingAddresseeIds(text);
  if (addressees.includes(botUserId)) return false;
  return addressees.some((id) => id !== botUserId && registeredAgentIds.has(id));
}

// ENG-4789: should this agent add the 👀 ack reaction to an inbound
// message? Decoupled from decideSlackEngagement so auto-followed thread
// replies still get the visual "I saw it" feedback even when the agent
// will silently skip the reply. Bot reactions don't produce app_mention
// or message events so they can't feed the cascade ENG-4595 fixed —
// only the response-timer's "fell on the floor" reply could.
export interface AckReactionInputs {
  channel: string | undefined;
  ts: string | undefined;
}

export function decideSlackAckReaction(input: AckReactionInputs): boolean {
  return Boolean(input.channel && input.ts);
}

// ── High-trust slash-command allowlist (ENG-6235) ─────────────────────────────
//
// `SLACK_ALLOWED_USERS` is the diagnostic / high-trust slash-command
// allowlist. It gates `/restart` and `/investigate` — commands that can
// bounce the agent or expose its raw terminal — and NOTHING ELSE. It must
// never gate ordinary conversation: `sender_policy`
// (decideSenderPolicyForward) is the single authority for who may talk to
// the agent, and unlike this allowlist it is UI-visible, logged, and
// declines politely. The old ENG-4475-era blanket message gate that lived
// inline in the Socket Mode handler silently dropped every message from a
// non-listed user (no log, no decline, invisible in the policy UI) and was
// removed in ENG-6235 — see ENG-6236 for the broader consolidation.
//
// `/restart` opens up when the allowlist is UNSET (empty ⇒ anyone may
// restart, the documented default for a host with no diagnostic allowlist
// configured) and otherwise admits only listed users. `/investigate` is
// stricter — it requires a non-empty allowlist — and is enforced at its own
// call site.
//
// Extracted as a pure predicate so the command-gating semantics are testable
// without standing up the Socket Mode handler, and so the allowlist has
// exactly one named consumer on the message path.
export function isRestartSenderAllowed(
  allowedUsers: ReadonlySet<string>,
  userId: string | undefined,
): boolean {
  if (allowedUsers.size === 0) return true;
  return userId != null && allowedUsers.has(userId);
}

// ── Sender policy (label-based) ───────────────────────────────────────────────
//
// Augmented agents embed Slack message metadata on every outbound message so
// peer agents can verify the sender is a known Augmented agent on the same
// team. Humans posting through the Slack UI cannot include message metadata
// (the metadata API requires an OAuth token tied to the Slack app), so the
// label acts as a lightweight capability gate.
//
// The metadata shape posted by the sending agent:
//   { event_type: 'augmented_agent_message',
//     event_payload: { augmented_team_id: '<slug>', augmented_agent_id: '<uuid>' } }

export type SenderPolicyMode =
  | 'all'
  | 'agents_only'
  | 'team_only'
  | 'team_agents_only'
  | 'manager_only';

export interface SenderPolicy {
  mode: SenderPolicyMode;
  /**
   * Required when mode === 'team_agents_only' / 'team_only'.
   *
   * ENG-10685: `manager_only` no longer consults it — that mode admits one
   * principal by user id and never reaches the team-scoped label check. The
   * field is still POPULATED for it by the provisioner and still required by
   * `buildSlackSenderPolicyFromEnv`, deliberately: narrowing the env contract
   * would make a newer manager stop sending a var an older MCP still throws
   * without, and this change must not be able to take a channel down at boot.
   */
  teamId?: string;
  /**
   * ENG-5842: principal Slack user_id required when mode === 'manager_only'.
   * The principal is the ONLY admitted sender (ENG-10685) — the same-team
   * agent carve-out this comment used to describe was removed, because it made
   * "manager only" mean "manager, plus every agent on the team". Missing
   * principal_id with mode=manager_only fails closed (drops ALL inbound, human
   * and agent alike).
   */
  principalSlackUserId?: string;
  /**
   * ENG-5871: team-member Slack user_ids when mode === 'team_only'. The
   * filter admits inbound when evt.user matches any entry in this list
   * OR when the message carries a same-team Augmented agent label. Empty
   * or absent list with mode=team_only is the documented fail-closed
   * posture for humans (admit only same-team agents via label) —
   * transient 1-person-team where the human hasn't linked Slack yet, or
   * a fresh org with no team membership wired up.
   */
  teamPrincipalSlackUserIds?: ReadonlyArray<string>;
  /**
   * ENG-5843: when true, the filter additionally drops inbound from external
   * Slack Connect workspaces (matched on `evt.team !== homeTeamId`). Composes
   * with the mode check: the mode admits first, then internal_only narrows.
   * `internal_only + mode:'all'` means "any sender, home workspace only."
   */
  internalOnly?: boolean;
  /**
   * ENG-5843: the bot's home Slack workspace id (from the bot install
   * `team.id` returned by auth.test). Required when `internalOnly` is true;
   * missing homeTeamId with internalOnly:true fails closed (drops all
   * inbound, since the filter can't tell internal from external).
   */
  homeTeamId?: string;
  /**
   * ENG-7786: Slack user_ids of registered peer agents (their `bot_user_id`,
   * the same identities `parsePeersEnv` registers from SLACK_PEERS). When
   * provided, a message admitted via the Augmented LABEL must ALSO be authored
   * by a registered sender (`evt.user` in this set) - binding the otherwise
   * spoofable label to a real, registered identity. The label is metadata the
   * sender attaches to their own message, so on its own it only proves "someone
   * posted augmented_agent_message metadata", not that the sender is a genuine
   * agent. When undefined (older manager, pre-wiring) the label is trusted on
   * content alone, preserving today's fleet behaviour until the registry is
   * wired.
   */
  registeredAgentSlackUserIds?: ReadonlySet<string>;
}

export type SenderPolicyDecision =
  | { forward: true }
  | { forward: false; reason: 'sender_policy' };

/** Extract the Augmented label from a Slack message event, if present. */
function extractAugmentedSlackLabel(
  evt: SlackMessageEvent,
): { teamId?: string; agentId?: string } | null {
  if (evt.metadata?.event_type !== 'augmented_agent_message') return null;
  return {
    teamId: evt.metadata.event_payload?.['augmented_team_id'] as string | undefined,
    agentId: evt.metadata.event_payload?.['augmented_agent_id'] as string | undefined,
  };
}

/**
 * Mode-only decision (pre-internal_only). Factored out so the composed gate
 * below can layer internal_only on top without duplicating the mode logic.
 *
 * ENG-5842 / ENG-10685: manager_only admits ONE counterparty — the resolved
 * principal, matched label-free on `evt.user`. It used to compose two axes,
 * also admitting any same-team Augmented agent via the label, which made it
 * identical to `team_agents_only` on the agent side and made the name false.
 * The agent axis is gone; `team_agents_only` is the mode that has it.
 */
function decideModeForward(
  evt: SlackMessageEvent,
  policy: SenderPolicy,
): SenderPolicyDecision {
  if (policy.mode === 'all') return { forward: true };

  if (policy.mode === 'manager_only') {
    // ENG-10685: ONE permitted counterparty, and nothing else. There is no
    // path 2.
    //
    // There used to be: a same-team Augmented agent fell through to the label
    // check below and was admitted, which made this mode identical to
    // `team_agents_only` on the agent axis. So an agent set to "manager only"
    // was reachable by every other agent on its team — not what the name says,
    // and not what ADR-0105's own comparison table says either ("manager-only:
    // exactly ONE permitted counterparty").
    //
    // Brad, 2026-09-18, asked directly whether peer agents should still reach a
    // manager-only agent: "No — nobody but the manager." No peer coordination,
    // no delegation from a lead agent, no teammate agent answering its cards.
    // An operator who wants the agent axis open has `team_agents_only`, which
    // is what this mode's second path silently was.
    return policy.principalSlackUserId && evt.user === policy.principalSlackUserId
      ? { forward: true }
      : { forward: false, reason: 'sender_policy' };
  }

  if (policy.mode === 'team_only') {
    // ENG-5871 Path 1: direct DM/mention from any resolved team member.
    // Empty/absent list ⇒ no human admitted, fall through to the label
    // check (which admits same-team Augmented agents).
    if (
      policy.teamPrincipalSlackUserIds &&
      policy.teamPrincipalSlackUserIds.length > 0 &&
      typeof evt.user === 'string' &&
      policy.teamPrincipalSlackUserIds.includes(evt.user)
    ) {
      return { forward: true };
    }
    // ENG-5871 Path 2: same-team Augmented agent — shares the label-check
    // below with team_agents_only / manager_only.
  }

  const label = extractAugmentedSlackLabel(evt);
  if (!label) return { forward: false, reason: 'sender_policy' };

  // ENG-7786: bind the label to a registered sender identity. Every path that
  // reaches here admits BECAUSE of the label (agents_only, team_agents_only,
  // team_only path 2 — manager_only no longer reaches here at all, ENG-10685).
  // The label alone is spoofable - it
  // is metadata the sender attaches to their own message - so when the registry
  // is wired, require the authored `evt.user` to be a known peer agent's
  // bot_user_id. Undefined registry = pre-wiring compat (trust the label).
  if (policy.registeredAgentSlackUserIds !== undefined) {
    if (typeof evt.user !== 'string' || !policy.registeredAgentSlackUserIds.has(evt.user)) {
      return { forward: false, reason: 'sender_policy' };
    }
  }

  // team_agents_only requires the team_id to match. team_only (ENG-5871)
  // piggybacks on the same check — same-team agents are admitted, cross-team
  // aren't.
  //
  // ENG-10685 removed `manager_only` from this list, and removing it was not
  // cosmetic: the mode returns before the label check now, so leaving it here
  // would be a branch that can never run, reading as though the carve-out
  // survived.
  if (policy.mode === 'team_agents_only' || policy.mode === 'team_only') {
    if (!policy.teamId || label.teamId !== policy.teamId) {
      return { forward: false, reason: 'sender_policy' };
    }
  }

  return { forward: true };
}

/**
 * Additional sender-policy gate applied after `decideSlackMessageForward`.
 * When mode is 'all' and internal_only is unset this is a no-op so callers
 * can always invoke it safely.
 *
 * ENG-5843: composes `mode` (sender-type axis) with `internal_only`
 * (org-boundary axis). Both must admit for the message to forward. The
 * gates are independent — mode admits a labelled same-team agent
 * regardless of workspace, internal_only admits any sender from the home
 * workspace regardless of label. Together they narrow to the intersection.
 */
export function decideSenderPolicyForward(
  evt: SlackMessageEvent,
  policy: SenderPolicy,
): SenderPolicyDecision {
  // Internal-only first (cheaper, single field compare) — if the sender
  // is external the message never reaches the mode-axis label check.
  if (policy.internalOnly) {
    // Fail closed when the home workspace id wasn't injected by the
    // provisioner — the alternative is admitting EVERY message (no
    // home to compare against), which is the opposite of what
    // internal_only is supposed to do.
    if (!policy.homeTeamId) return { forward: false, reason: 'sender_policy' };
    if (evt.team !== policy.homeTeamId) return { forward: false, reason: 'sender_policy' };
  }
  return decideModeForward(evt, policy);
}
